// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Color } from '../../../Graphics';
import { PriceType } from '@shared/utils/History/CashItemUtils';
import { MAMode } from '../IndicatorConstants';
import { IndicatorScriptBase } from '../IndicatorScriptBase';
import { LineStyle } from '../IndicatorScriptBaseEnums';
import { InputParameterInteger } from '../InputParamaterClasses/InputParameterInteger';
import { InputParameterCombobox } from '../InputParamaterClasses/InputParameterCombobox';
export class KAMA extends IndicatorScriptBase {
    constructor() {
        super();
        this.ProjectName = 'Kaufman adaptive moving average';
        this.Comments = 'Kaufman Adaptive Moving Average';
        this.SetIndicatorLine('kama', Color.Gray, 1, LineStyle.SimpleChart);
        this.SetIndicatorLine('upTrendMarkers', Color.Blue, 5, LineStyle.HorizontalLine);
        this.SetIndicatorLine('downTrendMarkers', Color.Red, 5, LineStyle.HorizontalLine);
        this.SeparateWindow = false;
        this.periodAMA = 10;
        super.InputParameter(new InputParameterInteger('periodAMA', 'Period of KAMA', 1, 1, 9999));
        this.nfast = 2.0;
        super.InputParameter(new InputParameterInteger('nfast', '#Fast', 2, 1, 9999));
        this.nslow = 30.0;
        super.InputParameter(new InputParameterInteger('nslow', '#Slow', 3, 1, 9999));
        this.G = 2.0;
        super.InputParameter(new InputParameterInteger('G', undefined, 4, 1, 9999));
        this.dK = 2.0;
        super.InputParameter(new InputParameterInteger('dK', undefined, 5, 1, 9999));
        this.SourcePrice = PriceType.Close;
        super.InputParameter(new InputParameterCombobox('SourcePrice', 'Sources prices for MA', 6, [
            { Close: PriceType.Close },
            { Open: PriceType.Open },
            { High: PriceType.High },
            { Low: PriceType.Low },
            { Typical: PriceType.Typical },
            { Medium: PriceType.Medium },
            { Weighted: PriceType.Weighted }
        ]));
        this.AMA_Trend_Type = AmaTrendType.Fixed;
        super.InputParameter(new InputParameterCombobox('AMA_Trend_Type', 'KAMA Trend Type', 7, [
            { Fixed: AmaTrendType.Fixed },
            { Average: AmaTrendType.Average }
        ]));
        this.AMA2 = [];
        this.SumAMA = [];
    }
    createInternalIndicators() {
        const getPrice = (offset) => {
            if (this.CurrentData.Count <= this.periodAMA) {
                return 0;
            }
            return Math.abs(this.CurrentData.getPriceByType(this.SourcePrice, offset) -
                this.CurrentData.getPriceByType(this.SourcePrice, offset + 1));
        };
        this.noiseMAIndicator = this.Indicators.iMAOnArray(this.CurrentData, getPrice, this.periodAMA, MAMode.SMA);
        return [this.noiseMAIndicator];
    }
    Init() {
        this.IndicatorShortName(this.GetIndicatorShortName());
        this.SetIndexDrawBegin(0, this.periodAMA);
    }
    UpdateIndexDrawBegin() {
        this.SetIndexDrawBegin(0, this.periodAMA);
    }
    GetIndicatorShortName() {
        return 'KAMA(' + this.periodAMA + ';' + this.nfast + ';' + this.nslow + ';' + this.G + ';' + this.dK + ')';
    }
    getCustomName() {
        return 'KAMA (' + this.ProjectName + ')';
    }
    NextBar() {
        super.NextBar();
        if (this.AMA_Trend_Type === AmaTrendType.Average) {
            this.AMA2.unshift(0);
            this.SumAMA.unshift(0);
        }
    }
    OnQuote() {
        var _a;
        super.OnQuote();
        if (this.CurrentData.Count - 1 < this.periodAMA) {
            this.SetValue(0, this.CurrentData.getPriceByType(this.SourcePrice));
            return;
        }
        const slowSC = 2.0 / (this.nslow + 1);
        const fastSC = 2.0 / (this.nfast + 1);
        const dFS = fastSC - slowSC;
        const noiseAvg = this.noiseMAIndicator.GetValue();
        const Noise = noiseAvg * this.periodAMA;
        const ER = Noise !== 0
            ? Math.abs(this.CurrentData.getPriceByType(this.SourcePrice) -
                this.CurrentData.getPriceByType(this.SourcePrice, this.periodAMA)) / Noise
            : 0;
        const SSC = Math.min(Math.pow(ER * dFS + slowSC, this.G), 1);
        const prevAma = this.GetValue(0, 1);
        const ama = this.CurrentData.getPriceByType(this.SourcePrice) * SSC + prevAma * (1 - SSC);
        this.SetValue(0, ama);
        const pointSize = ((_a = this.CurrentData.Instrument) === null || _a === void 0 ? void 0 : _a.PointSize) ||
            this.getDecimalPlaces(this.CurrentData.getPriceByType(this.SourcePrice));
        let level = 0;
        if (this.AMA_Trend_Type === AmaTrendType.Fixed) {
            level = this.dK * pointSize;
        }
        else {
            this.AMA2[0] = ama * ama + this.AMA2[1];
            this.SumAMA[0] = ama + this.SumAMA[1];
            const SredneeAMA = (this.SumAMA[0] - this.SumAMA[this.periodAMA]) / this.periodAMA;
            const SumKvadratAMA = this.AMA2[0] - this.AMA2[this.periodAMA];
            const dispersion = SumKvadratAMA / this.periodAMA - SredneeAMA * SredneeAMA;
            const StdAMA = dispersion >= 0 ? Math.sqrt(dispersion) : 0;
            level = this.dK * StdAMA;
        }
        if (Math.abs(ama - prevAma) > level) {
            if (ama - prevAma > 0) {
                this.SetValue(1, ama - 15 * pointSize);
            }
            else {
                this.SetValue(2, ama + 15 * pointSize);
            }
        }
    }
    getDecimalPlaces(value) {
        if (!Number.isFinite(value))
            return 0;
        const text = value.toString();
        const dotIndex = text.indexOf('.');
        return dotIndex === -1 ? 0 : text.length - dotIndex - 1;
    }
}
export var AmaTrendType;
(function (AmaTrendType) {
    AmaTrendType[AmaTrendType["Fixed"] = 0] = "Fixed";
    AmaTrendType[AmaTrendType["Average"] = 1] = "Average";
})(AmaTrendType || (AmaTrendType = {}));
